<script setup lang="ts">
import { ref } from 'vue';
import {
  FhText,
  FhCard,
  FhAccordion,
  FhAccordionPanel,
  FhIcon,
  FhDialog,
  FhTitle,
  FhButton,
} from '@fareharbor-com/beacon-vue';
import {
  calendarMonth,
  code,
  externalLink,
  bookmark,
} from '@fareharbor-com/beacon-vue/icons';
import BaseFlexbox from '@/components/ui/BaseFlexbox/BaseFlexbox.vue';

defineOptions({
  name: 'FaqHelp',
});

const calendarPreviewDialogRef = ref<InstanceType<typeof FhDialog>>();
const bookingPreviewDialogRef = ref<InstanceType<typeof FhDialog>>();

const handleCalendarPreviewClick = () => {
  calendarPreviewDialogRef.value?.open();
};

const handleBookingPreviewClick = () => {
  bookingPreviewDialogRef.value?.open();
};
</script>

<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-50 to-white">
    <header class="bg-primary-600 shadow-lg">
      <BaseFlexbox
        direction="column"
        align="center"
        justify="center"
        class="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8"
      >
        <FhTitle
          tag="h2"
          variant="display-xl"
          class="text-center mb-4"
          :color="'primary-default'"
        >
          FHDN Help Page
        </FhTitle>
      </BaseFlexbox>
    </header>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex flex-col lg:flex-row gap-8">
        <div
          class="bg-white rounded-lg shadow-md p-6 sticky top-8 self-start max-h-[calc(100vh-4rem)] overflow-y-auto"
        >
          <FhTitle
            tag="h2"
            variant="display-xl"
            class="mb-4 pb-4"
            :color="'primary-default'"
          >
            Table of Contents
          </FhTitle>
          <ul class="space-y-3">
            <li
              v-for="(item, index) in [
                { id: 'next-steps', text: 'Next Steps' },
                { id: 'integrations', text: 'Integrations' },
                { id: 'booking-links', text: 'Booking Links' },
                { id: 'calendar-links', text: 'Calendar Links and Script' },
                { id: 'embeds', text: 'Embeds' },
                { id: 'lightframe', text: 'Lightframe and Plugin' },
                { id: 'payouts', text: 'Payouts' },
                { id: 'reporting', text: 'Reporting and Analytics' },
                { id: 'ga4', text: 'Google Analytics' },
                { id: 'faqs', text: 'FAQs' },
              ]"
              :key="index"
            >
              <a
                :href="`#${item.id}`"
                class="block text-primary-600 hover:text-primary-800 transition-colors duration-200"
              >
                <FhText variant="body-md" :color="'default'">
                  {{ index + 1 }}. {{ item.text }}
                </FhText>
              </a>
            </li>
          </ul>
        </div>

        <main class="flex-1 space-y-8">
          <!-- Next Steps Section -->
          <FhCard
            id="next-steps"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Next Steps
            </FhTitle>
            <div class="p-6 rounded-lg border space-y-4">
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                Choose tours and activities you'd like to promote from the FHDN
                Marketplace. Use the search bar to filter by region, country,
                state, or city to limit your search. You may also search for a
                company name, item name, or ID. Once you have found activities
                you'd like to promote, you can paste the links directly on your
                website or post.
              </FhText>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                <strong>Note:</strong>
                Connect your bank account to your FareHarbor Dashboard. This
                ensures that you will receive your commission on a monthly
                basis!
              </FhText>
              <div
                class="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg"
              >
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-blue-700"
                >
                  <strong>Tip:</strong>
                  You can preview any tour or activity by clicking
                  <em>Preview</em>
                  .
                </FhText>
              </div>
            </div>
          </FhCard>

          <!-- Integrations Section -->
          <FhCard
            id="integrations"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Integrations
            </FhTitle>
            <div class="p-6 rounded-lg border space-y-4">
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                Once you've decided on the activities you want to resell, click
                <strong>Copy Referral Link</strong>
                to get a unique customized
                <em>url</em>
                that can be integrated on your website. This link will
                automatically save to your clipboard, and FareHarbor will be
                notified that you have generated the link.
              </FhText>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                <strong>Note:</strong>
                This unique customized
                <em>url</em>
                will be tagged with your company as the affiliate, and it is
                very important that you do
                <em>not</em>
                change this
                <em>url</em>
                .
              </FhText>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                Integrate this URL on your website and start generating
                bookings! Once you have completed your initial integration,
                please notify
                <a
                  href="mailto:strategicpartnerships@fareharbor.com"
                  class="text-primary-600 hover:text-primary-800"
                >
                  strategicpartnerships@fareharbor.com
                </a>
                with the subpage URL so we can review for quality control.
              </FhText>
              <div
                class="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg"
              >
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-blue-700"
                >
                  <strong>Tip:</strong>
                  If you are integrating a lot of links, we recommend that you
                  paste the URL (PC: CTRL + v | Mac: COMMAND + v) into a Google
                  Sheet, and share this document with your FareHarbor point of
                  contact or strategicpartnerships@fareharbor.com
                </FhText>
              </div>
            </div>
          </FhCard>

          <!-- Booking Links Section -->
          <FhCard
            id="booking-links"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Booking Links
            </FhTitle>
            <div class="p-6 rounded-lg border space-y-6">
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                The easiest and most versatile way to integrate in any website,
                page, QRCodes and e-mails. With these unique links you can add
                them to anywhere that it will be tracked.
              </FhText>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FhCard
                  tag="div"
                  size="sm"
                  :elevated="false"
                  class="p-6 border rounded-lg transition-shadow duration-200"
                >
                  <BaseFlexbox
                    direction="row"
                    align="center"
                    justify="center"
                    gap="2"
                    class="mb-4"
                  >
                    <div class="text-primary-600">
                      <FhIcon :icon="bookmark" size="lg" />
                    </div>
                    <FhTitle tag="h3" variant="display-lg" :color="'default'">
                      Booking Link
                    </FhTitle>
                  </BaseFlexbox>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700 mb-4"
                  >
                    This link directs customers to the booking flow, showing
                    item details and availability.
                  </FhText>
                  <FhText variant="body-sm" :color="'default'" class="mt-2">
                    <pre
                      class="bg-gray-100 p-3 mt-2 rounded text-sm break-all whitespace-pre-wrap"
                    >
https://demo.fareharbor.com/embeds/book/rsaudeadventures/items/42494/?ref=affiliatename&amp;
asn=fhdn-eur&amp;asn-ref=affiliatename&amp;full-items=yes&amp;flow=no</pre
                    >
                  </FhText>
                  <div class="mt-4">
                    <FhButton
                      variant="secondary"
                      size="sm"
                      @click="handleBookingPreviewClick"
                      class="w-full"
                    >
                      Preview Booking Link
                      <FhIcon :icon="externalLink" class="ml-2" />
                    </FhButton>
                  </div>
                </FhCard>

                <FhCard
                  tag="div"
                  size="sm"
                  :elevated="false"
                  class="p-6 border rounded-lg transition-shadow duration-200"
                >
                  <BaseFlexbox
                    direction="row"
                    align="center"
                    justify="center"
                    gap="2"
                    class="mb-4"
                  >
                    <div class="text-primary-600">
                      <FhIcon :icon="calendarMonth" size="lg" />
                    </div>
                    <FhTitle tag="h3" variant="display-lg" :color="'default'">
                      Calendar Link
                    </FhTitle>
                  </BaseFlexbox>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700 mb-4"
                  >
                    This link directs customers straight to the calendar
                    view—bypassing the item details page. It's ideal when your
                    website already displays item information.
                  </FhText>
                  <FhText variant="body-sm" :color="'default'" class="mt-2">
                    <pre
                      class="bg-gray-100 p-3 mt-2 rounded text-sm break-all whitespace-pre-wrap"
                    >
https://demo.fareharbor.com/embeds/calendar/rsaudeadventures/items/42494/?ref=affiliatename&amp;
asn=fhdn-eur&amp;asn-ref=affiliatename&amp;full-items=yes&amp;flow=no&amp;ref=&amp;back=</pre
                    >
                  </FhText>
                  <div class="mt-4">
                    <FhButton
                      variant="secondary"
                      size="sm"
                      @click="handleCalendarPreviewClick"
                      class="w-full"
                    >
                      Preview Calendar Link
                      <FhIcon :icon="externalLink" class="ml-2" />
                    </FhButton>
                  </div>
                </FhCard>
              </div>
            </div>
          </FhCard>

          <!-- Scripts Section -->
          <FhCard
            id="scripts"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Scripts
            </FhTitle>
            <div class="p-6 rounded-lg border space-y-6">
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                For advanced integrations, you can use our scripts to embed
                booking functionality directly into your website.
              </FhText>

              <div class="grid grid-cols-1 gap-6">
                <FhCard
                  tag="div"
                  size="sm"
                  :elevated="false"
                  class="p-6 border rounded-lg transition-shadow duration-200"
                >
                  <BaseFlexbox
                    direction="row"
                    align="center"
                    justify="center"
                    gap="2"
                    class="mb-4"
                  >
                    <div class="text-primary-600">
                      <FhIcon :icon="code" size="lg" />
                    </div>
                    <FhTitle tag="h3" variant="display-lg" :color="'default'">
                      Calendar Script
                    </FhTitle>
                  </BaseFlexbox>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700 mb-4"
                  >
                    This script can be embedded directly into your website to
                    show a detailed calendar view.
                  </FhText>
                  <FhText variant="body-sm" :color="'default'" class="mt-2">
                    <pre
                      class="bg-gray-100 p-3 mt-2 rounded text-sm break-all whitespace-pre-wrap"
                    >
&lt;script src="https://demo.fareharbor.com/embeds/script/calendar/rsaudeadventures/items/42494/?ref=affiliatename&amp;
asn=fhdn-eur&amp;asn-ref=affiliatename&amp;full-items=yes&amp;flow=no&amp;ref=&amp;back="&gt;&lt;/script&gt;</pre
                    >
                  </FhText>
                </FhCard>
              </div>
            </div>
          </FhCard>

          <!-- Embeds Section -->
          <FhCard
            id="embeds"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Embeds
            </FhTitle>
            <div class="p-6 rounded-lg border space-y-4">
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                In addition to book buttons you can also use a calendar embed or
                create a list of activities using our custom embed builder.
              </FhText>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                Please keep in mind the embed builder requires using custom code
                on your website. We recommend keeping custom embeds to no more
                than 12 activities.
              </FhText>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                In this embed generator you can customize colour, font size and
                order.
              </FhText>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                You can find additional integration tools here –
                <a
                  href="https://fhdntools.com/"
                  class="text-primary-600 hover:text-primary-800"
                >
                  https://fhdntools.com/
                </a>
              </FhText>
            </div>
          </FhCard>

          <!-- Lightframe Section -->
          <FhCard
            id="lightframe"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Maximize Conversions: Enable our Lightframe API
            </FhTitle>
            <div class="space-y-6">
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                The Lightframe is an online booking tool developed by
                FareHarbor, designed to provide your customers with an
                intuitive, secure booking process. To show the booking as a
                pop-up while keeping customers on your site, follow the
                instructions below.
              </FhText>

              <div class="p-6 rounded-lg">
                <FhTitle
                  tag="h3"
                  variant="display-lg"
                  class="text-lg font-medium mb-4"
                >
                  Lightframe script
                </FhTitle>
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700 mb-4"
                >
                  Copy the code below and paste it right above your page's
                  closing &lt;/body&gt; tag. This is required for the Lightframe
                  booking overlay to function. More information can be found on
                  the
                  <a
                    href="https://fareharbor.com/help/website/resources/lightframe-api/"
                    class="text-primary-600 hover:text-primary-800"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Advanced Lightframe API Usage
                  </a>
                  help page.
                </FhText>
                <div class="bg-white p-4 rounded-lg border">
                  <pre
                    class="text-sm text-gray-600 break-all whitespace-pre-wrap"
                  >
&lt;script type="text/javascript" src="https://fareharbor.com/embeds/api/v1/?autolightframe=yes"&gt;&lt;/script&gt;</pre
                  >
                </div>
              </div>

              <div class="p-6 rounded-lg">
                <FhTitle
                  tag="h3"
                  variant="display-lg"
                  class="text-lg font-medium mb-4"
                >
                  FareHarbor Plugin
                </FhTitle>
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700"
                >
                  To automatically include the code on your WordPress website,
                  you can download and install our FareHarbor Plugin.
                </FhText>
              </div>
            </div>
          </FhCard>

          <!-- Payouts Section -->
          <FhCard
            id="payouts"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Payouts
            </FhTitle>
            <div class="space-y-6">
              <div class="p-6 rounded-lg border space-y-4">
                <FhText variant="body-md" class="text-gray-700">
                  As an affiliate of the FareHarbor Distribution Network (FHDN),
                  you earn a 15% commission on all confirmed bookings you
                  generate for a tour provider.
                </FhText>
                <FhText variant="body-md" class="text-gray-700">
                  Your commission will be transferred to your bank account
                  approximately on the 10th business day of the month following
                  the activity taking place.
                </FhText>
                <FhText variant="body-md" class="text-gray-700">
                  For example: In January, you generated a booking for an
                  activity taking place in February. Your payout for this
                  booking will be deposited on the 10th business day of March.
                </FhText>
                <FhText variant="body-md" class="text-gray-700">
                  Please note: for canceled and partially-refunded bookings, you
                  will receive your commission proportionally to the amount the
                  tour provider has been paid.
                </FhText>
              </div>

              <div class="p-6 rounded-lg border">
                <FhTitle tag="h3" variant="display-lg" class="mb-4">
                  Minimum Fee
                </FhTitle>
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700"
                >
                  There is no minimal threshold to be met to be part of the
                  program, and no fee to participate. This means that the full
                  15% commission will be deposited into your bank account for
                  bookings you generate.
                </FhText>
              </div>

              <div class="p-6 rounded-lg border">
                <FhTitle tag="h3" variant="display-lg" class="mb-4">
                  Refunds
                </FhTitle>
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700"
                >
                  If a booking is canceled and fully refunded, no commission
                  will be paid out for the booking. If the booking is partially
                  refunded you will still receive the 15% commission on the
                  unrefunded balance.
                </FhText>
              </div>

              <div class="p-6 rounded-lg border">
                <FhTitle tag="h3" variant="display-lg" class="mb-4">
                  Getting Paid: Complete the Payment Setup Checklist
                </FhTitle>
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700 mb-4"
                >
                  To receive payouts from the FareHarbor Distribution Network,
                  you must complete the
                  <strong>Payment Setup Checklist</strong>
                  in your affiliate Dashboard. We recommend doing this as soon
                  as possible to ensure that there are no delays in receiving
                  your commission.
                </FhText>

                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700 font-medium mb-2"
                >
                  To begin receiving payments:
                </FhText>
                <ol class="list-decimal ml-5 space-y-2 text-gray-700">
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Log in to your affiliate Dashboard.
                    </FhText>
                  </li>
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Go to
                      <strong>Settings</strong>
                      .
                    </FhText>
                  </li>
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Click
                      <strong>Bank &amp; Payments</strong>
                      .
                    </FhText>
                  </li>
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Complete the
                      <strong>Payment Setup Checklist</strong>
                      in the
                      <strong>Overview</strong>
                      tab.
                    </FhText>
                  </li>
                </ol>

                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700 mt-6"
                >
                  You must also fill in your contact and billing information for
                  our legal records.
                </FhText>

                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700 font-medium mb-2 mt-4"
                >
                  To provide this required information:
                </FhText>
                <ol class="list-decimal ml-5 space-y-2 text-gray-700">
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Login into your affiliate Dashboard.
                    </FhText>
                  </li>
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Go to
                      <strong>
                        Settings &gt; Info &amp; Policies &gt; Logo &amp;
                        Contact
                      </strong>
                      .
                    </FhText>
                  </li>
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Complete all fields in the
                      <strong>Contact Information</strong>
                      and
                      <strong>Billing Information for Invoicing</strong>
                      sections.
                    </FhText>
                  </li>
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Click
                      <strong>Save changes</strong>
                      .
                    </FhText>
                  </li>
                </ol>
              </div>

              <div class="p-6 rounded-lg border">
                <FhTitle tag="h3" variant="display-lg" class="mb-4">
                  Payout Timeline
                </FhTitle>
                <div class="flex justify-center">
                  <img
                    src="https://fhdn.fareharbor.com/wp-content/uploads/sites/4516/2023/11/screenshot_2023-11-10_at_11.33.01.png?w=1200"
                    alt="Payout Timeline Diagram"
                    class="w-full h-auto rounded-lg shadow-md max-w-4xl my-4"
                  />
                </div>
              </div>
            </div>
          </FhCard>

          <!-- Reporting Section -->
          <FhCard
            id="reporting"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Reporting and Analytics
            </FhTitle>
            <div class="p-6 rounded-lg border space-y-6">
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                As an affiliate of the FHDN, you can report on bookings you have
                referred to in the reports section of the affiliate dashboard.
              </FhText>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                To view reports, please login to your affiliate dashboard, go to
                Reports and choose FHDN Bookings (under Advanced).
              </FhText>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                This report can be useful in gathering important insights, such
                as:
              </FhText>
              <ul class="list-disc ml-5 space-y-2 text-gray-700">
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    Which experiences sell best on your website
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    Which days your website generates more bookings
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    An overview of your expected earnings
                  </FhText>
                </li>
              </ul>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                Reporting data will update in real time to provide the most
                accurate information available.
              </FhText>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                Depending upon which Report on option is selected when the
                report is run, a report can highlight different information:
              </FhText>
              <ul class="list-disc ml-5 space-y-2 text-gray-700">
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    Last booked date or Booking created date will return data
                    based on the day a booking was created.
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    Availability date will return data based on the day an
                    activity takes place. (We suggest running a report based on
                    availability date to view your expected earnings.)
                  </FhText>
                </li>
              </ul>
            </div>

            <div class="p-6 rounded-lg border mt-6">
              <FhTitle tag="h3" variant="display-lg" class="mb-4">
                Understanding Report Results
              </FhTitle>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700 mb-4"
              >
                Additional information about report results is included below.
                Use date range, filter, group by options, and booking columns to
                customize a report that works best for you.
              </FhText>
              <ul class="list-disc ml-5 space-y-2 text-gray-700">
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    <strong>Booking ID:</strong>
                    The booking's unique nine-digit Booking ID. (Note: If a
                    customer who booked on your website calls to ask if their
                    booking was made, please direct them to the tour operator.)
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    <strong>Created at:</strong>
                    The date and time the booking was created.
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    <strong>Canceled?:</strong>
                    Whether the booking was canceled by the customer. (Note: If
                    the booking was canceled and refunded, you will not receive
                    commission.)
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    <strong>Company:</strong>
                    The name of the associated tour operator.
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    <strong>Item Name:</strong>
                    The name of the tour or activity associated with the
                    booking. (Note: Item names are managed by the tour
                    operator.)
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    <strong>Availability Start At:</strong>
                    The date and time scheduled for the booked activity.
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    <strong># of Pax:</strong>
                    The number of customers associated with the booking.
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    <strong>Online Booking Reference:</strong>
                    A unique number or tag added to the book button for
                    identification purposes. For example, an online booking
                    reference can help identify which web page or campaign the
                    booking was generated from.
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    <strong>Invoice Total:</strong>
                    Your expected commission for the booking.
                  </FhText>
                </li>
                <li>
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-gray-700"
                  >
                    <strong>Payable to Affiliate:</strong>
                    The outstanding commission amount expected for the booking.
                    Once the invoice has been created in the provider's
                    Dashboard and the payment has been made, the amount will
                    appear as $0.
                  </FhText>
                </li>
              </ul>

              <div class="mt-8">
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700"
                >
                  Bookings can change at any time for a variety of reasons—
                  cancellations, refunds, rebookings, add-ons, etc. Expected
                  earnings listed in the
                  <strong>Payable to Affiliate</strong>
                  column may not always reflect the final commission amount
                  owed.
                </FhText>

                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700 mt-4"
                >
                  <strong>Note:</strong>
                  Your monthly commission report will always have the most
                  up-to-date and accurate commission amounts.
                </FhText>
              </div>
            </div>

            <div class="p-6 rounded-lg border mt-6">
              <FhTitle tag="h3" variant="display-lg" class="mb-4">
                Client Information
              </FhTitle>
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                Due to privacy reasons, we cannot provide any personally
                identifiable information (PII), including customer name or
                contact information.
              </FhText>
            </div>
          </FhCard>

          <!-- Google Analytics Section -->
          <FhCard
            id="ga4"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Google Analytics (GA4)
            </FhTitle>
            <div class="space-y-6">
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                You can track the sales that go through your website using our
                integration with Google Analytics. You will receive data
                regarding the tour booked and the subtotal. To start receiving
                this information, we need your GA4
                <strong>Measurement ID</strong>
                and the list of companies you are distributing.
              </FhText>

              <div class="p-6 rounded-lg">
                <FhTitle
                  tag="h3"
                  variant="display-lg"
                  class="text-lg font-medium mb-4"
                >
                  Getting Started
                </FhTitle>
                <ol class="list-decimal ml-5 space-y-4 text-gray-700">
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Create a Google Sheet with
                      <em>all</em>
                      the items you have integrated on your website.
                    </FhText>
                  </li>
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Set up GA4 and send us your
                      <a
                        href="#"
                        class="text-primary-600 hover:text-primary-800"
                      >
                        GA4 Measurement ID
                      </a>
                      . Information on finding your GA4 Measurement ID can be
                      found
                      <a
                        href="#"
                        class="text-primary-600 hover:text-primary-800"
                      >
                        here
                      </a>
                      .
                    </FhText>
                  </li>
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Send us the Google Sheet with the list of items and GA4 ID
                      using our
                      <a
                        href="#"
                        class="text-primary-600 hover:text-primary-800"
                      >
                        contact form
                      </a>
                      . Complete the contact form by selecting
                      <strong>Google Analytics</strong>
                      from the
                      <em>What do you need help with?</em>
                      list. Select
                      <strong>First Connection with Google Analytics</strong>
                      from the
                      <em>
                        What best describes your request? - Google Analytics
                      </em>
                      list.
                    </FhText>
                  </li>
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      Set up
                      <a
                        href="#"
                        class="text-primary-600 hover:text-primary-800"
                      >
                        FareHarbor Lightframe
                      </a>
                      on your website; if you cannot set up Lightframe, tracking
                      will not work.
                    </FhText>
                  </li>
                  <li>
                    <FhText variant="body-md" :color="'default'">
                      We will connect your GA4 ID with every company on the list
                      you send us.
                    </FhText>
                  </li>
                </ol>
              </div>

              <div class="p-6 rounded-lg">
                <FhTitle
                  tag="h3"
                  variant="display-lg"
                  class="text-lg font-medium mb-4"
                >
                  To confirm that your integration is properly configured
                </FhTitle>
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700 mb-4"
                >
                  Go to
                  <strong>Reports</strong>
                  in GA4. Select
                  <strong>Engagement</strong>
                  and click
                  <strong>Pages and Screens</strong>
                  . Confirm that you are receiving information similar to that
                  displayed in the screenshot below.
                </FhText>
                <div class="p-2 rounded mb-4">
                  <FhText
                    variant="body-md"
                    :color="'default'"
                    class="text-blue-700"
                  >
                    <strong>TIP:</strong>
                    Use the search term
                    <em>"embeds"</em>
                    to narrow your results.
                  </FhText>
                </div>
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700 mt-6"
                >
                  If you received a booking you can find it on Reports > Report
                  Monetization > Ecommerce Purchases.
                </FhText>

                <div class="flex justify-center mt-6">
                  <img
                    src="https://fhdn.fareharbor.com/wp-content/uploads/sites/4516/2024/01/Screenshot-2024-01-11-at-09.45.12.png"
                    alt="Google Analytics Ecommerce Purchases Report"
                    class="w-full h-auto rounded-lg shadow-md max-w-4xl my-4"
                  />
                </div>
              </div>

              <div class="p-6 rounded-lg">
                <FhTitle
                  tag="h3"
                  variant="display-lg"
                  class="text-lg font-medium mb-4"
                >
                  Discrepancies between Google Analytics and Reports
                </FhTitle>
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700 mb-4"
                >
                  Sometimes there are some discrepancies between Google
                  Analytics and our reports. Below there are a few reasons why,
                  if you are not sure, reach out to us through our contact form.
                </FhText>

                <FhAccordion divider class="space-y-4">
                  <FhAccordionPanel name="ga-discrepancy-1">
                    <div class="font-medium">
                      Cancelled, Refunded or Rebooked Bookings
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        Bookings can be rebooked, cancelled or refunded and
                        therefore not be eligible for commission or for a lower
                        one.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="ga-discrepancy-2">
                    <div class="font-medium">
                      Non-commissionable Custom Fields
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        There are custom-fields (also known as Addons) which are
                        not commissionable, they are triggered on Google
                        Analytics. Examples of these non-commissionable custom
                        fields are Natural Parks Entry costs or fuel surcharges.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="ga-discrepancy-3">
                    <div class="font-medium">
                      Google Analytics is not set up properly
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        Sometimes Google Analytics might not be well set up on
                        your website. Check
                        <a
                          href="https://tagassistant.google.com"
                          class="text-primary-600 hover:text-primary-800"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Google Tag Assistant
                        </a>
                        to confirm that your tag is properly set up.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="ga-discrepancy-4">
                    <div class="font-medium">
                      Google Analytics 4 ID is not associated with the charter
                      company
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        Every time you add a new tour or experience we are
                        required to add your GA4 ID to that company dashboard,
                        make sure to reach us so it's up to date.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="ga-discrepancy-5">
                    <div class="font-medium">
                      Multiple instances of GA4 ID on charter company
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        If you see that it is triggering twice per booking, let
                        us know as your GA4 ID might have been added twice to
                        the same company.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="ga-discrepancy-6">
                    <div class="font-medium">
                      Taxes are not applicable on Google Analytics (Europe)
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        In Europe, commission includes tax, while in the United
                        States of America it is not inclusive. This discrepancy
                        can make the numbers harder to read in Google Analytics.
                        Some companies have set up tax to zero on their
                        dashboard which creates confusion.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="ga-discrepancy-7">
                    <div class="font-medium">Logging out of FareHarbor</div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        When someone makes a test booking and has to log out
                        from their dashboard, the unique identifier will be
                        lost.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="ga-discrepancy-8">
                    <div class="font-medium">
                      The FareHarbor Lightframe is not working
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        To be able to track the client steps in FareHarbor pages
                        you will need to activate Fareharbor Lightframe.
                      </FhText>
                    </template>
                  </FhAccordionPanel>
                </FhAccordion>
              </div>

              <div class="p-6 rounded-lg">
                <FhTitle
                  tag="h3"
                  variant="display-lg"
                  class="text-lg font-medium mb-4"
                >
                  Lightframe script
                </FhTitle>
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700 mb-4"
                >
                  Copy the code below and paste it right above your page's
                  closing &lt;/body&gt; tag. This is required for the Lightframe
                  booking overlay to function. More information can be found on
                  the
                  <a
                    href="https://fareharbor.com/help/website/resources/lightframe-api/"
                    class="text-primary-600 hover:text-primary-800"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Advanced Lightframe API Usage
                  </a>
                  help page.
                </FhText>
                <div class="bg-white p-4 rounded-lg border">
                  <pre
                    class="text-sm text-gray-600 break-all whitespace-pre-wrap"
                  >
&lt;script type="text/javascript" src="https://fareharbor.com/embeds/api/v1/?autolightframe=yes"&gt;&lt;/script&gt;</pre
                  >
                </div>
              </div>

              <div class="p-6 rounded-lg">
                <FhTitle
                  tag="h3"
                  variant="display-lg"
                  class="text-lg font-medium mb-4"
                >
                  FareHarbor Plugin
                </FhTitle>
                <FhText
                  variant="body-md"
                  :color="'default'"
                  class="text-gray-700"
                >
                  To automatically include the code on your WordPress website,
                  you can download and install our FareHarbor Plugin.
                </FhText>
              </div>
            </div>
          </FhCard>

          <!-- FAQs Section -->
          <FhCard
            id="faqs"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Frequently Asked Questions
            </FhTitle>

            <div class="space-y-6">
              <div class="p-6 rounded-lg border">
                <FhTitle tag="h3" variant="display-lg" class="mb-4">
                  Getting Started
                </FhTitle>
                <FhAccordion divider class="space-y-4">
                  <FhAccordionPanel name="faq1">
                    <div class="font-medium">
                      How do the referral links work?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        You can copy the booking link from the FHDN Directory
                        for an activity and place it on your website, social
                        media post, confirmation emails or anywhere else you
                        think someone might book an activity. When the link is
                        clicked and a booking takes place, the commission is
                        tracked via the link and you are tagged as the affiliate
                        that generated the booking.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq1.1">
                    <div class="font-medium">
                      How do I preview tours from the Directory?
                    </div>

                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        Right-click on the Preview Booking button and select
                        "Open link in incognito (or private) window". If you
                        open the link in your regular browser you will be
                        prompted to log out of your FareHarbor dashboard,
                        logging out can cause issues with the tagging in the
                        links.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq2">
                    <div class="font-medium">
                      Do I need to reach out to the tour providers to distribute
                      them?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        No, by opting into the FHDN and agreeing to the terms
                        and conditions tour providers have agreed that
                        affiliates on the network can distribute their products.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq3">
                    <div class="font-medium">
                      I do not see inventory in my area on the Directory but I
                      know FareHarbor works with tour providers in the area. Why
                      is that?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        FareHarbor has clients all over the world, but not all
                        have opted in to the FHDN to give commission on tour
                        sales. Contact us and we can work with you to get more
                        inventory on the network in your area. If you know a
                        tour provider that is interested in joining the network,
                        let us know! We are happy to reach out to chat with tour
                        providers to make them aware of the network's benefits
                        and help answer questions they might have.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq3.1">
                    <div class="font-medium">
                      I have my own direct contracts with your providers, can
                      you still honor those agreements?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        Yes! The FHDN may be used concurrently with any of your
                        existing arrangements and contracts. However, the value
                        add is that with the FHDN, you don't have to deal with
                        contracting or invoicing, saving you time and money in
                        the long run. You can have existing ASN buttons and FHDN
                        buttons alongside each other on your website.
                      </FhText>
                    </template>
                  </FhAccordionPanel>
                </FhAccordion>
              </div>

              <div class="p-6 rounded-lg border">
                <FhTitle tag="h3" variant="display-lg" class="mb-4">
                  Payouts and Reporting
                </FhTitle>
                <FhAccordion divider class="space-y-4">
                  <FhAccordionPanel name="faq4">
                    <div class="font-medium">
                      When do I receive the payments for the bookings I
                      generated as an affiliate?
                    </div>
                    <template #panel>
                      <FhText
                        variant="body-md"
                        class="p-4 space-y-4 text-default"
                      >
                        <p>
                          As a FareHarbor Distribution Network (FHDN) affiliate,
                          you are earning a 15% commission on all confirmed
                          bookings you generate for a tour provider. Your
                          commission will be transferred to your bank account
                          every 10th business day of the month following the
                          activity taking place.
                        </p>

                        <p>
                          <strong>Example:</strong>
                          In January, you generated a booking for an activity
                          taking place in February. Your payout for this booking
                          will be deposited on the 10th business day of March.
                        </p>

                        <p>
                          <strong>Please note:</strong>
                          For canceled and partially-refunded bookings, you will
                          receive your commission proportionally to the amount
                          the tour provider has been paid.
                        </p>
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq5">
                    <div class="font-medium">How does reporting work?</div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        FHDN affiliates can report on bookings associated with
                        their FareHarbor Dashboard. FHDN Bookings (located under
                        Reports > Advanced > FHDN Bookings) can be used to
                        gather important insights, such as:
                        <ul class="list-disc ml-5 mt-2">
                          <li>Which experiences sell best on your website</li>
                          <li>
                            Which days your website generates more bookings
                          </li>
                          <li>An overview of your expected earnings</li>
                        </ul>
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq5.1">
                    <div class="font-medium">How can I track my bookings?</div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        You can use GA4 to track the bookings that are generated
                        through your website. Reach out to
                        <a
                          href="mailto:strategicpartnerships@fareharbor.com"
                          class="text-primary-600 hover:text-primary-800"
                        >
                          strategicpartnerships@fareharbor.com
                        </a>
                        with the list of companies and items you are selling
                        along with your GA4 Stream ID and we can set up the
                        connection for you.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq5.2">
                    <div class="font-medium">
                      There is a booking in my GA4 conversions that is not
                      showing up on my monthly commission report, why?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        In most cases, it means that the booking was canceled.
                        Reach out to
                        <a
                          href="mailto:strategicpartnerships@fareharbor.com"
                          class="text-primary-600 hover:text-primary-800"
                        >
                          strategicpartnerships@fareharbor.com
                        </a>
                        with the booking details you have and we can
                        investigate.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq5.3">
                    <div class="font-medium">
                      Can I have access to the client's name or any other
                      contact details?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        Due to privacy reasons, we cannot provide any personally
                        identifiable information (PII), including customer name
                        or contact information.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq5.4">
                    <div class="font-medium">Is there a minimum or a fee?</div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        There is no minimal threshold to be met to be part of
                        the program, and no fee to participate. This means that
                        the full 15% commission will be deposited into your bank
                        account for bookings you generate.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq5.5">
                    <div class="font-medium">
                      What happens in the event of a refund?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        If a booking is canceled and fully refunded, no
                        commission will be paid out for the booking. If the
                        booking is partially refunded you will still receive the
                        15% commission on the unrefunded balance.
                      </FhText>
                    </template>
                  </FhAccordionPanel>
                </FhAccordion>
              </div>

              <div class="p-6 rounded-lg border">
                <FhTitle tag="h3" variant="display-lg" class="mb-4">
                  Integrations
                </FhTitle>
                <FhAccordion divider class="space-y-4">
                  <FhAccordionPanel name="faq6">
                    <div class="font-medium">
                      Can I use the images and content from the booking links to
                      promote activities?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        Yes, you can use the content provided on the item page,
                        as long as it's clear that the content is the tour
                        provider's and not yours.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq6.1">
                    <div class="font-medium">
                      FareHarbor has a calendar embed integration that isn't on
                      the Directory. How can I access this?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        You can either email
                        <a
                          href="mailto:strategicpartnerships@fareharbor.com"
                          class="text-primary-600 hover:text-primary-800"
                        >
                          strategicpartnerships@fareharbor.com
                        </a>
                        or you can use this tool to convert a book button to a
                        calendar embed –
                        <a
                          href="https://fhdntools.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-primary-600 hover:text-primary-800"
                        >
                          URL to calendar script
                        </a>
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq7">
                    <div class="font-medium">
                      The pop-up or lightbox is not working. How do I get
                      customers to stay on my site when booking?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        Make sure to integrate the Light Frame API, using one of
                        these ways:
                        <ul class="list-disc ml-5 mt-2 space-y-2">
                          <li>
                            Have a WordPress site? Download the free plug-in
                            FareHarbor for WordPress and enable
                            "Auto-lightframing".
                          </li>
                          <li>
                            Otherwise copy the code below and paste it right
                            before the closing &lt;/body&gt; tag of each page
                            that you integrate FareHarbor buttons or links on.
                            <pre
                              class="bg-gray-100 p-3 mt-2 rounded text-sm break-all whitespace-pre-wrap"
                            >
&lt;!-- FareHarbor Lightframe API -- do not remove -- see: https://fareharbor.com/help/website/resources/lightframe-api/ --&gt;
&lt;script src="https://fareharbor.com/embeds/api/v1/?autolightframe=yes"&gt;&lt;/script&gt;</pre
                            >
                          </li>
                        </ul>
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq7.1">
                    <div class="font-medium">
                      Do I need to communicate every time I integrate a link on
                      my website?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        Please notify
                        <a
                          href="mailto:strategicpartnerships@fareharbor.com"
                          class="text-primary-600 hover:text-primary-800"
                        >
                          strategicpartnerships@fareharbor.com
                        </a>
                        with the subpage URL so we can review for quality
                        control.
                      </FhText>
                    </template>
                  </FhAccordionPanel>

                  <FhAccordionPanel name="faq7.2">
                    <div class="font-medium">
                      How to choose the page in which the client goes after
                      completing the booking?
                    </div>
                    <template #panel>
                      <FhText variant="body-md" :color="'default'" class="p-4">
                        At the end of the URL there is "&amp;back=BACKLINK",
                        change the "BACKLINK" to the desired URL, including
                        "www". If left untouched, the page will redirect to the
                        URL the customer came from.
                      </FhText>
                    </template>
                  </FhAccordionPanel>
                </FhAccordion>
              </div>
            </div>
          </FhCard>

          <!-- Link Anatomy Section -->
          <FhCard
            id="link-anatomy"
            tag="section"
            size="md"
            :elevated="true"
            class="transition-transform duration-200"
          >
            <FhTitle
              tag="h2"
              variant="display-xl"
              class="mb-4"
              :color="'primary-default'"
            >
              Link structure and anatomy
            </FhTitle>
            <div class="p-6 rounded-lg border space-y-4">
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700"
              >
                Here is a breakdown of the structure of the booking links:
              </FhText>
              <!-- Add table here if needed -->
              <FhText
                variant="body-md"
                :color="'default'"
                class="text-gray-700 mt-4"
              >
                Visual representation:
              </FhText>
              <img
                src="https://fhdn.fareharbor.com/wp-content/uploads/sites/4516/2023/11/Link-Anatomy-3.jpg"
                alt="Link Anatomy Breakdown"
                class="w-full h-auto rounded-lg shadow-md"
              />
            </div>
          </FhCard>
        </main>
      </div>
    </div>
  </div>

  <FhDialog ref="bookingPreviewDialogRef" size="xl">
    <template #title>
      <FhTitle tag="h4" variant="display-lg">Booking Link Preview</FhTitle>
    </template>
    <template #content>
      <div class="h-[80vh]">
        <iframe
          src="https://demo.fareharbor.com/embeds/book/rsaudeadventures/items/42494/?ref=affiliatename&asn=fhdn-eur&asn-ref=affiliatename&full-items=yes&flow=no"
          class="w-full h-full"
          frameborder="0"
          title="Booking Link Preview"
          sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
          referrerpolicy="origin"
          credentialless
          crossorigin="anonymous"
        />
      </div>
    </template>
  </FhDialog>

  <FhDialog ref="calendarPreviewDialogRef" size="xl">
    <template #title>
      <FhTitle tag="h4" variant="display-lg">Calendar Link Preview</FhTitle>
    </template>
    <template #content>
      <div class="h-[80vh]">
        <iframe
          src="https://demo.fareharbor.com/embeds/calendar/rsaudeadventures/items/42494/?ref=affiliatename&asn=fhdn-eur&asn-ref=affiliatename&full-items=yes&flow=no&ref=&back="
          class="w-full h-full"
          frameborder="0"
          title="Calendar Link Preview"
          sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
          referrerpolicy="origin"
          credentialless
          crossorigin="anonymous"
        />
      </div>
    </template>
  </FhDialog>
</template>

<style scoped>
/* Add your component styles here */
:deep(.fh-dialog) {
  max-width: 80rem;
  width: 90vw;
  margin: 0 auto;
}

/* Smooth scrolling for anchor links */
html {
  scroll-behavior: smooth;
}

/* Add borders to all content sections */
.p-6.rounded-lg {
  border: 1px solid #e5e7eb;
}
</style>
